/**
 * Scrollable message list with selected state, snippets, and badges
 *
 * @module inbox/MessageList
 */

import React from 'react';
import { Flex, Box, Text, Button, LoadingOrbit, TruncatedText, TagBadge } from '@chatsdet/ui';
import { EnvelopeSimpleOpen, Paperclip } from '@phosphor-icons/react';
import { formatRelative } from './utils';
import type { InboxMessageListItem } from './types';

type MessageListProps = {
  messages: InboxMessageListItem[];
  selectedMessageId: string | null;
  onSelectMessage: (id: string) => void;
  isLoading: boolean;
  isError: boolean;
  totalItems: number;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  emptyMessage?: string;
  /** Show property badge on each message (useful in superadmin context) */
  showPropertyBadge?: boolean;
  /** Show sender initials avatar */
  showAvatar?: boolean;
};

function getInitials(name: string | null, address: string | null): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  if (address) {
    const local = address.split('@')[0];
    return (local?.slice(0, 2) ?? '??').toUpperCase();
  }
  return '??';
}

export function MessageList({
  messages,
  selectedMessageId,
  onSelectMessage,
  isLoading,
  isError,
  totalItems: _totalItems,
  page,
  totalPages,
  onPageChange,
  emptyMessage = 'No messages found.',
  showPropertyBadge = false,
  showAvatar = false,
}: MessageListProps) {
  if (isLoading) {
    return (
      <Flex align="center" justify="center" className="flex-1">
        <LoadingOrbit size="lg" />
      </Flex>
    );
  }

  if (isError) {
    return (
      <Flex align="center" justify="center" className="flex-1 px-4 text-center">
        <Text size="sm" className="text-destructive">
          Failed to load messages.
        </Text>
      </Flex>
    );
  }

  if (messages.length === 0) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        className="flex-1 px-4 text-center gap-2"
      >
        <EnvelopeSimpleOpen size={28} className="text-muted-foreground" />
        <Text size="sm" className="text-muted-foreground">
          {emptyMessage}
        </Text>
      </Flex>
    );
  }

  return (
    <>
      <Box className="flex-1 overflow-y-auto">
        <Flex direction="column">
          {messages.map((message) => {
            const isSelected = message.id === selectedMessageId;

            return (
              <button
                key={message.id}
                type="button"
                className={[
                  'text-left px-4 py-3 transition-colors border-l-[3px]',
                  isSelected
                    ? 'bg-muted/60 border-primary'
                    : 'border-transparent hover:bg-muted/40',
                ].join(' ')}
                onClick={() => onSelectMessage(message.id)}
              >
                <Flex gap="sm" align="start">
                  {showAvatar && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center mt-0.5">
                      <Text size="xs" weight="medium" className="text-muted-foreground">
                        {getInitials(message.from.name, message.from.address)}
                      </Text>
                    </div>
                  )}
                  <Flex direction="column" gap="xs" className="min-w-0 flex-1">
                    <Flex align="center" justify="between" gap="md">
                      <TruncatedText text={message.subject ?? '(No subject)'} className="font-medium text-sm text-foreground" />
                      <Text size="xs" className="text-muted-foreground whitespace-nowrap">
                        {formatRelative(message.receivedAt)}
                      </Text>
                    </Flex>
                    <TruncatedText text={message.from.name ?? message.from.address ?? 'Unknown sender'} className="text-xs text-muted-foreground" />
                    {message.snippet && (
                      <TruncatedText text={message.snippet} lines={2} className="text-xs text-muted-foreground" />
                    )}
                    <Flex align="center" gap="xs" wrap>
                      {message.hasAttachments && (
                        <TagBadge
                          name="Attachment"
                          color="gray"
                          size="sm"
                          icon={<Paperclip size={12} />}
                        />
                      )}
                      {showPropertyBadge && message.property?.name && (
                        <TagBadge name={message.property.name} color="gray" hideDot size="sm" />
                      )}
                    </Flex>
                  </Flex>
                </Flex>
              </button>
            );
          })}
        </Flex>
      </Box>

      {totalPages > 1 && (
        <Flex
          align="center"
          justify="between"
          className="px-3 py-2 border-t border-border/60 bg-card/60"
        >
          <Button
            variant="ghost"
            size="sm"
            isDisabled={page <= 1}
            onPress={() => onPageChange(Math.max(1, page - 1))}
          >
            Previous
          </Button>
          <Text size="xs" className="text-muted-foreground">
            Page {page} of {totalPages}
          </Text>
          <Button
            variant="ghost"
            size="sm"
            isDisabled={page >= totalPages}
            onPress={() => onPageChange(Math.min(totalPages, page + 1))}
          >
            Next
          </Button>
        </Flex>
      )}
    </>
  );
}
